<template>
    <div class="d-sm-flex align-items-center justify-content-between mb-2">
        <h2 class="main-title fs-28 mb-0" style="display: inline;">Analytics Dashboard</h2>
        <div class="d-flex gap-2">
            <button type="button" class="btn btn-outline-primary" @click="refreshPowerBI">
                <i class="ri-refresh-line"></i> Refresh
            </button>
            <button type="button" class="btn btn-outline-secondary" @click="toggleFullscreen">
                <i class="ri-fullscreen-line"></i> Fullscreen
            </button>
        </div>
    </div>
    
    <div class="row g-3">
        <div class="col-sm-12">
            <div class="card card-one">
                <div class="card-header">
                    <!-- <h6 class="card-title">Power BI Analytics</h6>
                    <div class="d-flex align-items-center gap-2">
                        <span class="badge bg-success" v-if="powerBIStatus === 'connected'">
                            <i class="ri-check-line"></i> Connected
                        </span>
                        <span class="badge bg-warning" v-else-if="powerBIStatus === 'loading'">
                            <i class="ri-loader-4-line"></i> Loading...
                        </span>
                        <span class="badge bg-danger" v-else>
                            <i class="ri-error-warning-line"></i> Error
                        </span>
                    </div> -->
                </div>
                <div class="card-body p-0">
                    <div id="powerbi-container" class="powerbi-embed-container">
                        <template v-if="powerBIStatus === 'loading'">
                            <div class="d-flex justify-content-center align-items-center" style="height: 600px;">
                                <div class="text-center">
                                    <div class="spinner-border text-primary mb-3" role="status">
                                        <span class="visually-hidden">Loading...</span>
                                    </div>
                                    <h5>Loading Power BI Dashboard...</h5>
                                    <p class="text-muted">Authenticating and loading your analytics...</p>
                                </div>
                            </div>
                        </template>
                        
                        <template v-else-if="powerBIStatus === 'error'">
                            <div class="d-flex justify-content-center align-items-center" style="height: 600px;">
                                <div class="text-center">
                                    <i class="ri-error-warning-line text-danger" style="font-size: 4rem;"></i>
                                    <h5 class="mt-3">Unable to Load Dashboard</h5>
                                    <p class="text-muted">{{ errorMessage }}</p>
                                    <div class="mt-3">
                                        <button class="btn btn-primary me-2" @click="initializePowerBI">
                                            <i class="ri-refresh-line"></i> Retry
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </template>
                        
                        <div v-show="powerBIStatus !== 'error'" class="powerbi-frame-container">
                            <div id="powerbi-embed" class="powerbi-iframe"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import powerBIService from '@/services/powerBIService.js';

export default {
    data() {
        return {
            powerBIStatus: 'loading',
            errorMessage: '',
            embedToken: null,
            config: {
                workspaceId: 'bcb18a0f-e701-4e30-88a6-ddea7062043c',
                reportId: '68f0e693-e55f-4252-9eb5-0a39c1d0041b',
                tenantId: '94dbcc7c-6e32-4329-a59a-3fb79b6fb70e',
                pageId: '74173e067628fbf15e6e'
            }
        };
    },
    
    async mounted() {
        try {
            powerBIService.setStore(this.$store);
            
            if (!this.$store.getters.token) {
                this.powerBIStatus = 'error';
                this.errorMessage = 'Please log in to access Power BI analytics';
                return;
            }

            // Load Power BI JavaScript SDK first
            try {
                if (!window.powerbi) {
                    const sdkUrls = [
                        'https://microsoft.github.io/PowerBI-JavaScript/dist/powerbi.min.js',
                        'https://cdn.jsdelivr.net/npm/powerbi-client@2.19.1/dist/powerbi.min.js',
                        'https://app.powerbi.com/powerbi-client/powerbi.min.js'
                    ];

                    let loaded = false;
                    for (const url of sdkUrls) {
                        try {
                            await new Promise((resolve, reject) => {
                                const script = document.createElement('script');
                                script.src = url;
                                script.async = true;
                                script.crossOrigin = 'anonymous';
                                script.onload = () => {
                                    loaded = true;
                                    resolve();
                                };
                                script.onerror = () => reject(new Error(`Failed to load from ${url}`));
                                document.head.appendChild(script);
                            });
                            if (loaded && window.powerbi) {
                                console.log('Power BI SDK loaded successfully from', url);
                                break;
                            }
                        } catch (err) {
                            console.warn(`Failed to load SDK from ${url}:`, err);
                            // Continue to next URL
                        }
                    }

                    if (!window.powerbi) {
                        throw new Error('Failed to load Power BI SDK from all sources');
                    }
                }
            } catch (error) {
                console.error('Failed to load Power BI SDK:', error);
                this.powerBIStatus = 'error';
                this.errorMessage = 'Failed to load Power BI SDK. Please try refreshing the page or using a different browser.';
                return;
            }

            // Wait for the next tick to ensure template is rendered
            await this.$nextTick();
            
            // Now initialize Power BI
            await this.initializePowerBI();
        } catch (error) {
            console.error('Mounting error:', error);
            this.powerBIStatus = 'error';
            this.errorMessage = error.message;
        }
    },
    
    beforeUnmount() {
        const embedContainer = document.getElementById('powerbi-embed');
        if (embedContainer && window.powerbi) {
            window.powerbi.reset(embedContainer);
        }
    },
    
    methods: {
        async initializePowerBI() {
            console.log('Initializing Power BI Direct Embed...');
            this.powerBIStatus = 'loading';
            this.errorMessage = '';
            
            try {
                if (!this.$store.getters.user || !this.$store.getters.token) {
                    throw new Error('User not authenticated');
                }
                
                // Get embed token
                console.log('Fetching embed token...');
                this.embedToken = await powerBIService.getEmbedToken();
                
                if (!this.embedToken) {
                    throw new Error('Failed to get embed token');
                }
                
                console.log('Got embed token:', this.embedToken.slice(0, 10) + '...');
                
                // Ensure container exists
                const embedContainer = document.getElementById('powerbi-embed');
                if (!embedContainer) {
                    throw new Error('Embed container not found - please try refreshing the page');
                }

                // Configure the embed with numeric values
                const embedConfig = {
                    type: 'report',
                    id: this.config.reportId,
                    embedUrl: `https://app.powerbi.com/reportEmbed?reportId=${this.config.reportId}&groupId=${this.config.workspaceId}`,
                    accessToken: this.embedToken,
                    tokenType: 1,
                    permissions: 7,
                    settings: {
                        filterPaneEnabled: true,
                        navContentPaneEnabled: true,
                        background: 1
                    },
                    viewMode: 0  // 0 = View mode, 1 = Edit mode
                };

                console.log('Embedding report...');
                
                // Verify SDK is loaded and functioning
                if (!window.powerbi || typeof window.powerbi.embed !== 'function') {
                    throw new Error('Power BI SDK not properly initialized');
                }

                // Create the embed instance
                const report = window.powerbi.embed(embedContainer, embedConfig);
                
                // Handle report events
                report.on('loaded', () => {
                    console.log('Report has loaded successfully');
                    this.powerBIStatus = 'connected';
                });

                report.on('rendered', () => {
                    console.log('Report has rendered successfully');
                    this.powerBIStatus = 'connected';
                });

                report.on('error', (event) => {
                    console.error('Report error:', event.detail);
                    this.powerBIStatus = 'error';
                    this.errorMessage = event.detail.message || 'Error loading report';
                    
                    // Log additional error information
                    if (event.detail) {
                        console.error('Detailed error:', {
                            message: event.detail.message,
                            errorCode: event.detail.errorCode,
                            detailedMessage: event.detail.detailedMessage
                        });
                    }
                });

            } catch (error) {
                console.error('Power BI initialization error:', error);
                this.powerBIStatus = 'error';
                this.errorMessage = error.message;
                throw error;
            }
        },
        
        async refreshPowerBI() {
            this.powerBIStatus = 'loading';
            try {
                await powerBIService.refreshReport();
                await this.initializePowerBI();
                console.log('Power BI refreshed successfully');
            } catch (error) {
                console.error('Refresh error:', error);
                this.powerBIStatus = 'error';
                this.errorMessage = 'Failed to refresh dashboard';
            }
        },
        
        toggleFullscreen() {
            const container = document.getElementById('powerbi-container');
            if (!document.fullscreenElement) {
                if (container.requestFullscreen) {
                    container.requestFullscreen();
                } else if (container.webkitRequestFullscreen) {
                    container.webkitRequestFullscreen();
                }
            } else {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                }
            }
        }
    }
};
</script>

<style scoped>
.powerbi-embed-container {
    position: relative;
    width: 100%;
    min-height: 600px;
    background-color: #f8f9fa;
    border-radius: 0.375rem;
}

.powerbi-frame-container {
    width: 100%;
    height: 600px;
    position: relative;
    display: block;
    visibility: visible;
    opacity: 1;
}

.powerbi-iframe {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    border: none !important;
    display: block !important;
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden !important;
    z-index: 1 !important;
    visibility: visible !important;
    opacity: 1 !important;
}

.powerbi-embed-container:fullscreen {
    background-color: white;
    padding: 20px;
}

.powerbi-embed-container:fullscreen .powerbi-iframe {
    height: calc(100vh - 40px);
}

@media (max-width: 768px) {
    .powerbi-embed-container {
        min-height: 400px;
    }
    
    .powerbi-frame-container {
        height: 400px;
    }
}
</style>